import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';

import { FlagIcon } from '../Flag';
import { Icon } from '../Icon';
import { Typography } from '../Typography';
import { useFavouritesActions } from '../../providers/FavouritesProvider';
import { useNavigationBarData } from '../NavigationBar/NavigationBarProvider';
import { useHeatMapSelectedLocations } from '../HeatMapExperience/HeatMapSelectedLocationsProvider';
import { usePoiSelectedLocations } from '../PointOfInterestExperience/PoiSelectedLocationsProvider';
import { useMapData } from '../../map-data/use-map-data';
import { theme } from '../../styles/theme';

import { MapInfoCard } from './MapInfoCard';

type Props = {
  countryCode: string;
  countryName: string;
  totalNationalProgrammes?: number;
  totalRegionalProgrammes?: number;
  totalStudiesProgrammes?: number;
  totalClinicalProgrammes?: number;
  closeCard: (value: boolean) => void;
  children?: JSX.Element | JSX.Element[];
  currentExperience?: 'poi' | 'heatmap';
  minWidth?: string;
};

export const CountryCard: FC<Props> = ({
  countryName,
  countryCode,
  totalNationalProgrammes,
  totalRegionalProgrammes,
  totalStudiesProgrammes,
  totalClinicalProgrammes,
  closeCard,
  children,
  currentExperience,
  minWidth,
}) => {
  const { t } = useTranslation();
  const { toggleHeatmapFavourite, isHeatmapFavourited } =
    useFavouritesActions();
  const { setSelectedExperience } = useNavigationBarData();
  const { setSelectedCountryCode, setShowCountryCard } =
    useHeatMapSelectedLocations();
  const { setSelectedCountryName, setShowCountryCard: setShowPoiCountryCard } =
    usePoiSelectedLocations();
  const { countryHeatMapMetaData, poiCountryData } = useMapData();

  // Check if the country exists in the target map view
  const hasHeatMapData = !!countryHeatMapMetaData?.[countryCode];
  const hasPoiData = !!poiCountryData?.countries?.find(
    c => c.name === countryName,
  );

  const favouriteIcon = (
    <div
      role="button"
      tabIndex={0}
      onClick={e => {
        e.stopPropagation();
        toggleHeatmapFavourite(countryCode);
      }}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          toggleHeatmapFavourite(countryCode);
        }
      }}
      aria-label={
        isHeatmapFavourited(countryCode)
          ? 'Remove from favourites'
          : 'Add to favourites'
      }
      style={{
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: 4,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Icon
        variant={isHeatmapFavourited(countryCode) ? 'HeartFilled' : 'Heart'}
        width={18}
        height={18}
        style={{
          color: isHeatmapFavourited(countryCode) ? '#e63946' : '#6a6a6a',
        }}
      />
    </div>
  );

  const headerFields = [
    {
      label: '',
      value: countryName,
      icon: favouriteIcon,
    },
  ];

  const defaultContent = (
    <Typography variant="p2">
      <ul>
        <li style={{ marginBottom: 0 }}>
          {t('card.nationalProgrammes')}:{' '}
          <span style={{ color: '#5A5A5A' }}>
            {totalNationalProgrammes ?? 0}
          </span>
        </li>
        <li style={{ marginBottom: 0 }}>
          {t('card.regionalProgrammes')}:{' '}
          <span style={{ color: '#5A5A5A' }}>
            {totalRegionalProgrammes ?? 0}
          </span>
        </li>
        <li style={{ marginBottom: 0 }}>
          {t('card.implementationStudies')}:{' '}
          <span style={{ color: '#5A5A5A' }}>
            {totalStudiesProgrammes ?? 0}
          </span>
        </li>
        <li style={{ marginBottom: 0 }}>
          {t('card.clinicalTrials')}:{' '}
          <span style={{ color: '#5A5A5A' }}>
            {totalClinicalProgrammes ?? 0}
          </span>
        </li>
      </ul>
    </Typography>
  );

  const handlePolicyMapClick = () => {
    setSelectedExperience('heatMap');
    setSelectedCountryCode(countryCode);
    setShowCountryCard(true);
  };

  const handleImplementationMapClick = () => {
    setSelectedExperience('pinMap');
    setSelectedCountryName(countryName);
    setShowPoiCountryCard(true);
  };

  const createNavButton = (
    hasData: boolean,
    onClick: () => void,
    icon: 'World' | 'Map',
    labelKey: string,
  ) =>
    hasData ? (
      <Box
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick();
          }
        }}
        aria-label={t(labelKey)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer',
          color: theme.colors.link,
          fontSize: '0.875rem',
        }}
      >
        <Icon variant={icon} width={16} height={16} />
        {t(labelKey)}
      </Box>
    ) : null;

  const policyMapButton = createNavButton(
    hasHeatMapData,
    handlePolicyMapClick,
    'World',
    'card.policyMap',
  );

  const implementationMapButton = createNavButton(
    hasPoiData,
    handleImplementationMapClick,
    'Map',
    'card.implementationMap',
  );

  const cardBody = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        color: '#6a6a6a',
      }}
    >
      <FlagIcon countryCode={countryCode} />
      {children ?? defaultContent}
    </Box>
  );

  return (
    <MapInfoCard
      closeCard={closeCard}
      headerFields={headerFields}
      width={minWidth ?? '14rem'}
      actionButton={
        currentExperience === 'poi'
          ? policyMapButton
          : currentExperience === 'heatmap'
          ? implementationMapButton
          : undefined
      }
    >
      {cardBody}
    </MapInfoCard>
  );
};
